/**
 * Formatters Utility
 *
 * Date and price formatting functions.
 */
import { parseLocalDate } from './date';

/**
 * Format date to Danish locale string
 * Example: "15. februar 2026" or "15. februar 2026 kl. 09:00"
 */
export function formatDate(
  dateString: string,
  options?: { includeTime?: boolean }
): string {
  try {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    
    if (options?.includeTime) {
      const timeStr = date.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${dateStr} kl. ${timeStr}`;
    }
    
    return dateStr;
  } catch {
    return dateString;
  }
}

/**
 * Format date to short Danish format
 * Example: "15/2-26"
 */
export function formatDateShort(dateString: string): string {
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}-${year}`;
  } catch {
    return dateString;
  }
}

/**
 * Format date and time
 * Example: "15. februar 2026 kl. 09:00"
 */
export function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${dateStr} kl. ${timeStr}`;
  } catch {
    return dateString;
  }
}

/**
 * Strip HTML tags and decode entities to plain text. Used for preview
 * surfaces (e.g. line-clamped card descriptions) where rich-text rendering
 * would visually break the fixed-line layout.
 */
export function stripHtml(html: string): string {
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Format price in DKK
 * Example: "2.500 kr." or "Gratis"
 */
export function formatPrice(amount: number): string {
  if (amount === 0) {
    return 'Gratis';
  }
  
  // Format with thousand separators
  const formatted = new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  
  return `${formatted} kr.`;
}

/**
 * Format time range
 * Example: "09:00 - 16:00"
 */
export function formatTimeRange(startTime: string, endTime?: string): string {
  if (endTime) {
    return `${startTime} - ${endTime}`;
  }
  return startTime;
}

/**
 * Format month and year in Danish
 * Example: "Marts 2026"
 */
export function formatMonthYear(dateString: string): string {
  try {
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return dateString;
  }
}

/**
 * Get relative time string
 * Example: "For 2 dage siden"
 */
export function getRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'I dag';
    } else if (diffDays === 1) {
      return 'I går';
    } else if (diffDays < 7) {
      return `For ${diffDays} dage siden`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `For ${weeks} ${weeks === 1 ? 'uge' : 'uger'} siden`;
    } else {
      return formatDate(dateString);
    }
  } catch {
    return dateString;
  }
}

const DA_WEEKDAYS = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];
const DA_MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** Design-stil dato: "ons 2. jul 2026" fra "YYYY-MM-DD". */
export function fmtDate(yyyymmdd: string): string {
  const dt = parseLocalDate(yyyymmdd);
  return `${DA_WEEKDAYS[dt.getDay()]} ${dt.getDate()}. ${DA_MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}

/** Træk Y/M/D/H/M ud i Europe/Copenhagen — deterministisk uanset maskinens TZ. */
function cphParts(iso: string): { year: number; month: number; day: number; hour: string; minute: string } {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { year: Number(p.year), month: Number(p.month), day: Number(p.day), hour: p.hour, minute: p.minute };
}

/** Design-stil timestamp-dato: "11. jun 2026". */
export function fmtTimestampDate(iso: string): string {
  const { year, month, day } = cphParts(iso);
  return `${day}. ${DA_MONTHS[month - 1]} ${year}`;
}

/** Design-stil timestamp-tid: "14:22". */
export function fmtTimestampTime(iso: string): string {
  const { hour, minute } = cphParts(iso);
  return `${hour}:${minute}`;
}

/** Klokkeslæt fra API ("HH:mm:ss" eller "HH:mm") → "18:00". */
export function fmtTime(hms: string): string {
  return hms.slice(0, 5);
}

/** Design-stil pris: "Gratis" eller "DKK 2.400". */
export function fmtPrice(amount: number): string {
  if (amount === 0) return 'Gratis';
  return `DKK ${amount.toLocaleString('da-DK')}`;
}

const DA_MONTHS_LONG = [
  'januar', 'februar', 'marts', 'april', 'maj', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'december',
];

/**
 * Normalisér dansk telefonnummer: behold cifre, fjern ét ledende '45' (landekode).
 * Spejler backendens normalisering (kontrakt §5.1) og designets `MDATA.norm`.
 */
export function normalizePhone(phone: string | null | undefined): string {
  return (phone || '').replace(/\D/g, '').replace(/^45/, '');
}

/** Dansk visning: "23 32 28 27". Uændret hvis ikke 8 cifre. */
export function fmtPhone(phone: string): string {
  const n = normalizePhone(phone);
  return n.length === 8 ? n.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4') : phone;
}

/** Lang fødselsdag: "14. marts 1988" fra "YYYY-MM-DD". `—` ved tom/ugyldig. */
export function fmtBirthday(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()}. ${DA_MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Kort dato: "1. sep 2023" fra "YYYY-MM-DD". `—` ved tom/ugyldig. */
export function fmtMemberSince(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()}. ${DA_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Heltal i dansk gruppering: 2400 → "2.400". Afrunder. */
export function fmtCount(n: number): string {
  return Math.round(n).toLocaleString('da-DK');
}
